import { useCallback, useEffect, useRef, useState } from 'react'
import type { FrameNotesDto } from '@praesto/shared'
import { api } from '../api.js'

/** Wie lange nach dem letzten Tastendruck gewartet wird, bevor gespeichert wird. */
const SAVE_DEBOUNCE_MS = 800

export interface Notes {
  /** Notiz eines Frames; leer, wenn keine hinterlegt ist. */
  get: (frameId: string) => string
  set: (frameId: string, text: string) => void
  /** Frames mit Notiz — für die Markierung in der Liste. */
  has: (frameId: string) => boolean
}

/**
 * Sprechernotizen eines Boards.
 *
 * Bewusst nicht im Yjs-Dokument: Das geht bei Präsentationsbeginn vollständig
 * an jeden Gast, eine Notiz darin läge also im Browser des Publikums. Sie
 * liegen stattdessen in Postgres und sind nur über den angemeldeten Zugriff auf
 * das Board zu bekommen.
 *
 * Preis dieser Entscheidung: keine gleichzeitige Bearbeitung wie bei
 * Board-Inhalten. Wer zuletzt schreibt, gewinnt — dieselbe Grenze, die für
 * Textelemente ohnehin schon gilt.
 */
export function useNotes(boardId: string, enabled: boolean): Notes {
  const [notes, setNotes] = useState<FrameNotesDto>({})
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    api
      .notes(boardId)
      .then((loaded) => {
        if (!cancelled) setNotes(loaded)
      })
      .catch(() => {
        // Fehlende Notizen sind kein Grund, das Board nicht zu zeigen.
      })
    return () => {
      cancelled = true
    }
  }, [boardId, enabled])

  // Beim Verlassen die noch offenen Schreibvorgänge nicht verlieren: Der Timer
  // wird abgeräumt, der Text ist aber schon im Zustand — deshalb hier sofort
  // hinterherschicken.
  const pending = useRef(new Map<string, string>())
  useEffect(() => {
    const open = pending.current
    const running = timers.current
    return () => {
      for (const timer of running.values()) clearTimeout(timer)
      running.clear()
      for (const [frameId, text] of open) {
        void api.setNote(boardId, frameId, text).catch(() => {})
      }
      open.clear()
    }
  }, [boardId])

  const set = useCallback(
    (frameId: string, text: string) => {
      setNotes((current) => ({ ...current, [frameId]: text }))
      pending.current.set(frameId, text)

      const running = timers.current.get(frameId)
      if (running) clearTimeout(running)
      timers.current.set(
        frameId,
        setTimeout(() => {
          timers.current.delete(frameId)
          void api
            .setNote(boardId, frameId, text)
            .then(() => {
              // Nur löschen, wenn inzwischen nichts Neueres eingetippt wurde.
              if (pending.current.get(frameId) === text) pending.current.delete(frameId)
            })
            .catch(() => {
              // Bleibt in `pending` und geht beim Verlassen erneut raus.
            })
        }, SAVE_DEBOUNCE_MS),
      )
    },
    [boardId],
  )

  const get = useCallback((frameId: string) => notes[frameId] ?? '', [notes])
  const has = useCallback((frameId: string) => (notes[frameId] ?? '').trim() !== '', [notes])

  return { get, set, has }
}
